import { useAuth0 } from "@auth0/auth0-react";

import { EditorialNote } from "@/components/editorial/EditorialNote";
import { SectionHead } from "@/components/editorial/SectionHead";

const ENFORCE = (import.meta.env.VITE_AUTH0_ENFORCE ?? "true") !== "false";

export function Profile() {
  // Read directly from Auth0 — avoids the Zustand mirror's lag where the
  // store is still null on first render and the page flashes
  // "Anonymous reader" even for a signed-in user.
  const { logout, user: rawUser } = useAuth0();
  const user = rawUser
    ? {
        sub: rawUser.sub ?? "unknown",
        email: rawUser.email,
        name: rawUser.name,
        picture: rawUser.picture,
      }
    : null;

  const onLogout = () => {
    if (ENFORCE) {
      logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
      window.location.href = "/login";
    }
  };

  return (
    <div className="space-y-10">
      <SectionHead label="Profile" italicSuffix="account" />

      <div className="grid gap-6 md:grid-cols-[140px_1fr] md:items-start">
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user.name ?? "Avatar"}
            className="h-32 w-32 rounded-full border border-ruleline object-cover"
          />
        ) : (
          <div className="h-32 w-32 rounded-full border border-ruleline bg-parchment-50" />
        )}
        <div className="space-y-2">
          <div className="text-smallcaps text-warmgray-400">Signed in as</div>
          <h3 className="font-serif text-headline text-espresso-700">
            {user?.name ?? "Anonymous reader"}
          </h3>
          <p className="font-mono text-sm text-warmgray-500">{user?.email ?? "—"}</p>
          <p className="font-mono text-xs text-warmgray-400">sub: {user?.sub ?? "—"}</p>
        </div>
      </div>

      <EditorialNote title="Privacy">
        Your chat history is stored server-side, keyed to your Google account. Signing out clears
        the local browser cache only — your sessions, citations, artifacts, and the reasoning
        trail behind each answer remain in the ledger and reappear the moment you sign back in
        with the same account.
      </EditorialNote>

      <button
        type="button"
        onClick={onLogout}
        className="border border-oxblood-500 px-6 py-2 font-serif text-base text-oxblood-500 transition-colors hover:bg-oxblood-500 hover:text-parchment-50"
      >
        Sign out
      </button>
    </div>
  );
}
